import { useEffect, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { getErrorMessage } from '../../lib/errors';
import { readNextPath, withNext } from '../../lib/nextPath';
import { AuthCard } from './AuthCard';
import { AuthAlert, AuthField, AuthSubmit, PasswordRules } from './AuthField';
import { LegalLinks } from './LegalLinks';
import { useAuth } from './useAuth';

/**
 * Self-serve sign-up is closed while Autopostmate is in internal testing. Flip this
 * to reopen it; the form and the Supabase call below are untouched.
 */
const REGISTRATION_OPEN = true;
const CONTACT_EMAIL = 'info@thekaizenglobal.com';
const CLOSED_TOAST_MS = 10_000;

export function SignupPage() {
  const { signUp } = useAuth();
  const location = useLocation();
  // Where the confirm link and the sign-in after it should land: an invite
  // opened signed out comes back to itself.
  const next = readNextPath(location);
  const isInvite = next.startsWith('/invite/');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [isClosedToastOpen, setIsClosedToastOpen] = useState(false);

  useEffect(() => {
    if (!isClosedToastOpen) return;
    const timer = setTimeout(() => setIsClosedToastOpen(false), CLOSED_TOAST_MS);
    return () => clearTimeout(timer);
  }, [isClosedToastOpen]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!REGISTRATION_OPEN) {
      setIsClosedToastOpen(true);
      return;
    }
    setIsSubmitting(true);
    try {
      await signUp(email, password, next);
      // Supabase either signs the user straight in (confirmations off) or sends
      // a confirmation email. The auth listener handles the first case; this
      // message covers the second.
      setIsSent(true);
    } catch (caught) {
      setError(getErrorMessage(caught, 'Sign-up failed. Try a different email.'));
    } finally {
      setIsSubmitting(false);
    }
  }

  const sentMessage = isInvite
    ? 'Check your inbox and confirm the address. Once confirmed, sign in to accept the invite.'
    : 'Check your inbox and confirm the address. Once confirmed, sign in to start onboarding.';

  return (
    <AuthCard
      tab={isSent ? undefined : 'signup'}
      icon={isSent ? 'mail' : undefined}
      title={isSent ? 'Check your email' : 'Start your 7-day trial'}
      description={
        isSent
          ? sentMessage
          : 'Solo free for seven days: 40 posts, LinkedIn publishing and the AI assistant. No card needed.'
      }
      footer={
        <>
          Already have an account? <Link to={withNext('/login', next)}>Sign in</Link>
        </>
      }
    >
      {isSent ? null : (
        <form className="auth-form" onSubmit={(event) => void handleSubmit(event)} noValidate>
          <AuthField
            label="Email"
            type="email"
            name="email"
            placeholder="you@company.com"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />

          <AuthField
            label="Password"
            type="password"
            name="password"
            placeholder="At least 8 characters"
            autoComplete="new-password"
            minLength={8}
            required
            hint={<PasswordRules password={password} />}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />

          {error ? <AuthAlert message={error} /> : null}

          <AuthSubmit label="Create account" isBusy={isSubmitting} />

          <LegalLinks action="creating an account" />
        </form>
      )}

      {/* Portalled to the body so the fixed toast stack is never clipped by the auth layout. */}
      {createPortal(
        <div className="undo-stack" role="status" aria-live="polite">
          {isClosedToastOpen ? (
            <article className="undo-toast undo-toast--notice">
              <p className="undo-toast__message">
                Autopostmate is currently only open to internal testers. Write to{' '}
                <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> to learn more.
              </p>
              <div className="undo-toast__actions">
                <button
                  className="undo-toast__close"
                  type="button"
                  aria-label="Dismiss"
                  onClick={() => setIsClosedToastOpen(false)}
                >
                  ×
                </button>
              </div>
            </article>
          ) : null}
        </div>,
        document.body,
      )}
    </AuthCard>
  );
}
